import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useRefreshEffect } from "@/context/RefreshContext";
import LeitstellenTelefone from "@/components/LeitstellenTelefone";
import { ShieldCheck, ScanSearch } from "lucide-react";

function KontrolleWidget({ endpoint, label, icon: Icon, successMsg, testid, now, thresholds }) {
  const [wert, setWert] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.get(endpoint).then((r) => setWert(r.data.wert)).catch(() => {});
  }, [endpoint]);

  useEffect(() => { load(); }, [load]);
  useRefreshEffect(load);

  const mark = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(endpoint);
      setWert(data.wert);
      toast.success(successMsg);
    } catch (e) {
      toast.error("Fehlgeschlagen");
    } finally {
      setBusy(false);
    }
  };

  const label2 = wert
    ? new Date(wert).toLocaleString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }) + " Uhr"
    : "Noch nicht erfasst";

  const th = thresholds || { yellow: 20, orange: 30, red: 45 };
  const minutes = wert ? Math.floor((now - new Date(wert).getTime()) / 60000) : null;
  const style =
    minutes == null
      ? { box: "border-zinc-800 hover:border-primary/50", accent: "text-primary", value: "text-zinc-100 group-hover:text-primary" }
      : minutes >= th.red
      ? { box: "border-red-500/60 bg-red-500/10", accent: "text-red-400", value: "text-red-300" }
      : minutes >= th.orange
      ? { box: "border-orange-500/60 bg-orange-500/10", accent: "text-orange-400", value: "text-orange-300" }
      : minutes >= th.yellow
      ? { box: "border-yellow-500/60 bg-yellow-500/10", accent: "text-yellow-400", value: "text-yellow-300" }
      : { box: "border-zinc-800 hover:border-primary/50", accent: "text-primary", value: "text-zinc-100 group-hover:text-primary" };

  const ago = (m) => {
    if (m >= 1440) return `vor ${Math.floor(m / 1440)} Tg.`;
    if (m >= 60) return `vor ${Math.floor(m / 60)} Std.`;
    return `vor ${m} Min.`;
  };

  return (
    <button
      data-testid={testid}
      onClick={mark}
      disabled={busy}
      title="Auf aktuelles Datum & Uhrzeit setzen"
      className={`group text-left bg-[#0C0E12] border rounded-sm px-4 py-2.5 transition-colors duration-150 disabled:opacity-60 ${style.box}`}
    >
      <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-500 flex items-center gap-1.5">
        <Icon className={`h-3.5 w-3.5 ${style.accent}`} /> {label}
      </div>
      <div className={`font-mono text-sm mt-1 transition-colors duration-150 ${style.value}`}>
        {busy ? "Aktualisiere…" : label2}
      </div>
      {minutes != null && (
        <div className={`font-mono text-[10px] mt-0.5 ${style.accent}`}>
          {ago(minutes)}
        </div>
      )}
    </button>
  );
}

export default function HeaderControls() {
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 15000);
    return () => clearInterval(t);
  }, []);

  return (
    <div className="flex flex-wrap items-stretch gap-2">
      <LeitstellenTelefone />
      <KontrolleWidget
        endpoint="/aussenzaunkontrolle"
        label="Letzte Außenzaunkontrolle"
        icon={ShieldCheck}
        successMsg="Außenzaunkontrolle aktualisiert"
        testid="azk-btn"
        now={now}
      />
      <KontrolleWidget
        endpoint="/innenhofkontrolle"
        label="Letzte Innenhof Kontrolle / Razzia"
        icon={ScanSearch}
        successMsg="Innenhofkontrolle / Razzia aktualisiert"
        testid="ihk-btn"
        now={now}
        thresholds={{ yellow: 180, orange: 1440, red: 4320 }}
      />
    </div>
  );
}
